import { useRef, useState, type TouchEvent } from "react";
import { driverHomeObservation, type DriverFollowStatus } from "../../core/follow";
import { monitorAge, monitorHidden, monitorSwipe, type MonitorSwipe, type PickupCalledOff } from "../../core/monitor";
import { phaseLabel } from "../../core/rider-journey";
import { headsignLabel, stationLabel } from "../../shared/labels";
import { formatClock, formatClockWithMeridiem } from "../../shared/time";
import { LAST_KNOWN_FIX_MS, mapFixAge } from "../../core/pickup-map";

function freshness(updatedAtMs: number, nowMs: number): { text: string; stale: boolean } {
	const minutes = Math.max(0, Math.floor((nowMs - updatedAtMs) / 60_000));
	if (minutes < 1) return { text: "Updated now", stale: false };
	if (minutes === 1) return { text: "Updated 1 minute ago", stale: false };
	return { text: `Updated ${minutes} minutes ago`, stale: minutes >= 5 };
}

function stateLabel(status: DriverFollowStatus): string {
	if (status.phase === "arrived") {
		return status.direction === "home" ? "At home" : "At school";
	}
	if (status.direction === "home" && status.phase === "at_home" && status.leftStart) {
		return "Left school";
	}
	return phaseLabel(status.phase, status.direction !== "home");
}

/** Teal ends a journey at either end; amber belongs to the next morning's start at home. */
export function riderMonitorPhase(status: DriverFollowStatus): DriverFollowStatus["phase"] {
	if (status.direction === "home" && status.phase === "at_home" && !status.leftStart) {
		return status.atShuttleStop ? "at_home" : "arrived";
	}
	return status.phase;
}

function trainDescription(status: DriverFollowStatus, timezone: string, nowMs: number): string | null {
	const atMorningHome = status.direction !== "home" && status.phase === "at_home";
	const arrivedHome = status.direction === "home" && status.phase === "arrived";
	if ((atMorningHome || arrivedHome) && status.homeName) {
		return status.homeName;
	}
	const atSchool = status.direction === "home" && status.phase === "at_home" && !status.leftStart;
	if ((atSchool || (status.direction !== "home" && status.phase === "arrived")) && status.finalLabel) {
		return status.finalLabel;
	}
	if (status.phase === "driving" && status.stationArrivalMs != null && status.boardingName) {
		return `${Math.max(0, Math.ceil((status.stationArrivalMs - nowMs) / 60_000))} min to ${stationLabel(status.boardingName)}`;
	}
	if (status.phase === "at_destination" && status.alighting) {
		return `at ${stationLabel(status.alighting.stopName)}`;
	}
	if (status.phase === "on_train") {
		const where = status.nextStopName
			? `${status.arriving ? "Arriving" : "Next"} ${stationLabel(status.nextStopName)}`
			: status.timingStatus
				? "Train location pending"
				: status.evidence === "inferred"
				? "In the Transbay Tube"
				: "Train location pending";
		const destinationName = status.destinationName ?? status.alighting?.stopName;
		const minutes = status.etaMs == null || status.etaMs <= nowMs ? null : Math.ceil((status.etaMs - nowMs) / 60_000);
		const destination = destinationName
			? minutes == null
				? `to ${stationLabel(destinationName)}${status.timingStatus === "overdue" || (status.etaMs != null && status.etaMs <= nowMs) ? " - arrival estimate overdue" : status.timingStatus === "unavailable" ? " - timing unavailable" : ""}`
				: `arriving ${stationLabel(destinationName)} in ${minutes} ${minutes === 1 ? "min" : "mins"}`
			: null;
		return [where, destination].filter(Boolean).join(" - ");
	}

	const waiting = status.phase === "at_station" || status.phase === "on_platform" || (status.direction === "home" && status.leftStart);
	if (!waiting || status.boardingMs == null) {
		return null;
	}
	const train = [status.route, status.headsign ? `to ${headsignLabel(status.headsign)}` : null].filter(Boolean).join(" ");
	const waitMinutes = Math.max(0, Math.ceil((status.boardingMs - nowMs) / 60_000));
	return `Likely${train ? ` - ${train}` : ""} - ${formatClock(status.boardingMs, timezone)} in ${waitMinutes} min`;
}

const CALLED_OFF_TEXT: Record<"rider" | "driver", string> = {
	rider: "Eben called off the pickup",
	driver: "You called off the pickup",
};

export function RiderMonitor({
	status,
	timezone,
	nowMs,
	calledOff = null,
	refreshError = null,
}: {
	status: DriverFollowStatus | null;
	timezone: string;
	nowMs: number;
	calledOff?: PickupCalledOff;
	refreshError?: string | null;
}) {
	const [dismissedAtMs, setDismissedAtMs] = useState<number | null>(null);
	const [swipe, setSwipe] = useState<MonitorSwipe>("none");
	const startRef = useRef<{ x: number; y: number; width: number } | null>(null);

	if (!status) {
		return refreshError ? (
			<section className="rider-monitor is-stale" aria-label="Eben's current journey">
				<strong>Eben · Journey unavailable</strong>
				<p className="rider-monitor__freshness" role="status">{refreshError}</p>
			</section>
		) : null;
	}
	const home = driverHomeObservation(status, nowMs);
	const readingAtMs = home?.atMs ?? status.updatedAtMs;
	const update = freshness(readingAtMs, nowMs);
	const train = home?.name ?? trainDescription(status, timezone, nowMs);
	const evidence = home ? "observed" : status.evidence;
	const evidenceLabel = evidence === "observed" && status.phase === "on_train" && status.trainObservation
		? "observed + declared"
		: evidence;
	const position = status.lastKnownPosition;
	const gpsAge = mapFixAge(position ?? null, nowMs, LAST_KNOWN_FIX_MS);

	// Waved away, but not gone: a phone that has been quiet for hours would otherwise leave no way
	// back to the last thing it said.
	if (monitorHidden(dismissedAtMs, readingAtMs)) {
		return (
			<button type="button" className="rider-monitor__stub" onClick={() => setDismissedAtMs(null)}>
				<span>Eben · hidden</span>
				<span className="rider-monitor__stubShow">Show</span>
			</button>
		);
	}

	const onTouchStart = (event: TouchEvent<HTMLElement>) => {
		const touch = event.touches[0];
		startRef.current = { x: touch.clientX, y: touch.clientY, width: event.currentTarget.clientWidth };
	};
	const onTouchMove = (event: TouchEvent<HTMLElement>) => {
		const start = startRef.current;
		if (!start) {
			return;
		}
		const touch = event.touches[0];
		setSwipe(monitorSwipe({ deltaX: touch.clientX - start.x, deltaY: touch.clientY - start.y, width: start.width }));
	};
	const onTouchEnd = () => {
		startRef.current = null;
		if (swipe === "hide") {
			setDismissedAtMs(readingAtMs);
		}
		setSwipe("none");
	};

	return (
		<section
			className={`rider-monitor ${update.stale || refreshError ? "is-stale" : ""}`}
			data-phase={home && status.phase !== "arrived" ? "at_home" : riderMonitorPhase(status)}
			data-age={monitorAge(readingAtMs, nowMs)}
			data-swipe={swipe}
			aria-label="Eben's current journey"
			onTouchStart={onTouchStart}
			onTouchMove={onTouchMove}
			onTouchEnd={onTouchEnd}
			onTouchCancel={onTouchEnd}
		>
			<div className="rider-monitor__body">
				<div className="rider-monitor__headline">
					<strong>Eben · {home ? "At home" : stateLabel(status)}</strong>
					{evidenceLabel ? <span className="rider-monitor__evidence">{evidenceLabel}</span> : null}
				</div>
				{train ? <p className="rider-monitor__train">{train}</p> : null}
				{status.trainObservation && <p className="rider-monitor__freshness">Train observed at {status.trainObservation.stationName} · {formatClockWithMeridiem(status.trainObservation.atMs, timezone)}</p>}
				{calledOff ? <p className="rider-monitor__pickup">{CALLED_OFF_TEXT[calledOff]}</p> : null}
				<p className="rider-monitor__freshness">{update.text}</p>
				{refreshError ? <p className="rider-monitor__freshness" role="status">{refreshError}</p> : null}
				{position && gpsAge !== "missing" && <p className="rider-monitor__freshness">GPS {gpsAge === "stale" ? "last seen " : ""}{Math.max(0, Math.floor((nowMs - position.fixAtMs) / 60_000))} min ago · ±{Math.round(position.accuracyMeters)}m{position.source === "manual-test" ? " · Manual test" : ""}</p>}
			</div>
			<button
				type="button"
				className="rider-monitor__hide"
				aria-label="Hide this reading until it changes"
				onClick={() => setDismissedAtMs(readingAtMs)}
			>
				Hide
			</button>
		</section>
	);
}